import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { ISalariesService } from './ISalariesService';
import { SalariesEndpoints } from './salariesEndpoints';
import {
    gelNumberOf,
    mapCommitPayrollRun,
    mapCreateEmployee,
    mapEmployeeList,
    mapPayrollRunDetail,
    mapPayrollRunList,
    mapPreviewPayrollRun,
    mapUpdateEmployee,
} from './salariesMapping';
import type {
    EmployeeListQuery,
    EmployeeWriteRequest,
    PayrollRunRequest,
} from './requests';
import type {
    CommitPayrollRunResponse,
    CreateEmployeeResponse,
    GetEmployeesResponse,
    GetPayrollRunResponse,
    GetPayrollRunsResponse,
    PreviewPayrollRunResponse,
    UpdateEmployeeResponse,
} from './responses';
import { parseGelToTetri } from '@/utils/money';

type SalariesServiceOptions = {
    baseUrl: string;
};

/**
 * Decimal GEL from the form → the JSON number the write edge takes.
 *
 * Routed through `parseGelToTetri` rather than `Number(value)` so the crossing
 * happens on the integer, exactly once, as it does everywhere else in this
 * client. A blank or unparseable field is `0` — which the server then accepts as
 * a real zero salary (an unpaid intern is a real employment) and reports as a
 * `ZERO_SALARY` line rather than the client inventing a message for it.
 */
const gelFieldToNumber = (value: string): number => {
    const parsed = parseGelToTetri(value);
    return gelNumberOf(parsed.ok ? parsed.tetri : 0);
};

/**
 * The employee write payload, shared by create and update.
 *
 * `id` travels on the create only — the update addresses the employee by path,
 * and the API's `UpdateEmployeeRequest` has no id field. Everything else is sent
 * on both: an update **replaces** every field, so omitting one is not "leave it
 * alone", it is "clear it".
 */
const employeePayload = (body: EmployeeWriteRequest) => ({
    name: body.name,
    personalId: body.personalId,
    position: body.position,
    hireDate: body.hireDate,
    // Explicitly null, never omitted: null is what returns a terminated employee
    // to ACTIVE, and a missing key would leave a departure dated that the user
    // just cleared.
    terminationDate: body.terminationDate,
    grossSalary: gelFieldToNumber(body.grossSalary),
    pensionParticipant: body.pensionParticipant,
    pensionNote: body.pensionNote,
});

/**
 * Real HTTP Salaries client — the register bound to `bos-api` (BOS-109).
 *
 * Every method maps its response through `salariesMapping`, which is the only
 * file that knows the wire's field names. Nothing here interprets a payload
 * beyond passing it to a mapper, and **nothing here posts to the ledger**: the
 * draft a commit returns is handed to the Journal Entry form, where a person
 * posts it under the journal's own validation and period gating.
 *
 * `locale` is sent on both run calls because the server's default is `ka`
 * regardless of the app's language; it localizes the draft's account names and
 * description. The register itself takes no locale — names come back as maps and
 * the display layer picks.
 */
export class SalariesService implements ISalariesService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: SalariesServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getEmployees(
        query: EmployeeListQuery,
    ): Promise<ApiResponse<GetEmployeesResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(
                SalariesEndpoints.employees({
                    status: query.status,
                    search: query.search,
                }),
            ),
        );
        return result.success
            ? { success: true, response: mapEmployeeList(result.response) }
            : result;
    }

    public async createEmployee(
        body: EmployeeWriteRequest,
    ): Promise<ApiResponse<CreateEmployeeResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(SalariesEndpoints.createEmployee(), {
                id: body.id,
                ...employeePayload(body),
            }),
        );
        return result.success
            ? { success: true, response: mapCreateEmployee(result.response) }
            : result;
    }

    public async updateEmployee(
        employeeId: string,
        body: EmployeeWriteRequest,
    ): Promise<ApiResponse<UpdateEmployeeResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.put(
                SalariesEndpoints.updateEmployee(employeeId),
                employeePayload(body),
            ),
        );
        return result.success
            ? { success: true, response: mapUpdateEmployee(result.response) }
            : result;
    }

    /**
     * `204` carries no body, so there is nothing to map — the envelope alone is
     * the answer. A refusal (`EMPLOYEE_HAS_RUNS`) comes back through the same
     * `ApiResponse` failure branch as every other write.
     */
    public deleteEmployee(employeeId: string): Promise<ApiResponse<void>> {
        return makeApiRequest<void>(
            this.client.delete(SalariesEndpoints.deleteEmployee(employeeId)),
        );
    }

    public async getPayrollRuns(): Promise<
        ApiResponse<GetPayrollRunsResponse>
    > {
        const result = await makeApiRequest<unknown>(
            this.client.get(SalariesEndpoints.payrollRuns()),
        );
        return result.success
            ? { success: true, response: mapPayrollRunList(result.response) }
            : result;
    }

    public async getPayrollRun(
        journalEntryId: string,
    ): Promise<ApiResponse<GetPayrollRunResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.get(SalariesEndpoints.payrollRun(journalEntryId)),
        );
        return result.success
            ? { success: true, response: mapPayrollRunDetail(result.response) }
            : result;
    }

    public async previewPayrollRun(
        body: PayrollRunRequest,
    ): Promise<ApiResponse<PreviewPayrollRunResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(SalariesEndpoints.previewRun(), {
                period: body.period,
                ...(body.locale ? { locale: body.locale } : {}),
            }),
        );
        return result.success
            ? { success: true, response: mapPreviewPayrollRun(result.response) }
            : result;
    }

    public async commitPayrollRun(
        body: PayrollRunRequest,
    ): Promise<ApiResponse<CommitPayrollRunResponse>> {
        const result = await makeApiRequest<unknown>(
            this.client.post(SalariesEndpoints.commitRun(), {
                period: body.period,
                ...(body.locale ? { locale: body.locale } : {}),
            }),
        );
        return result.success
            ? { success: true, response: mapCommitPayrollRun(result.response) }
            : result;
    }
}
